import { useEffect, useMemo, useState } from 'react';
import { DashboardService } from './service';
import { useDashboardContext } from './Context';
import { useEstabsContext } from '../../contexts/EstabsContext';
import { ServicosService } from '../servicos/Service';
import KpiCard from './KpiCard';

const service = new DashboardService();
const servicosService = new ServicosService();

export default function StockDashboard() {
    const { startAt, endAt } = useDashboardContext();
    const { estabSession } = useEstabsContext();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);
    const [services, setServices] = useState<any[]>([]);

    useEffect(() => {
        if (!estabSession?._id) return;
        servicosService.getByEstabId(estabSession._id).then((r) => setServices(r?.data || []));
    }, [estabSession?._id]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const response = await service.getEstoque({ startAt, endAt });
                setData(response.data);
            } catch (error) {
                console.error('Erro ao carregar dashboard de estoque', error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [startAt, endAt]);

    const serviceById = useMemo(() => {
        return services.reduce((acc: any, item: any) => {
            acc[item._id] = item;
            return acc;
        }, {});
    }, [services]);

    const topServices = useMemo(() => {
        const grouped: Record<string, { servicoId: string; totalCost: number; totalQuantity: number }> = {};
        (data?.consumptionByService || []).forEach((row: any) => {
            if (!row.servicoId) return;
            if (!grouped[row.servicoId]) {
                grouped[row.servicoId] = { servicoId: row.servicoId, totalCost: 0, totalQuantity: 0 };
            }
            grouped[row.servicoId].totalCost += row.totalCost || 0;
            grouped[row.servicoId].totalQuantity += row.totalQuantity || 0;
        });
        return Object.values(grouped).sort((a, b) => b.totalCost - a.totalCost).slice(0, 5);
    }, [data]);

    if (!data) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body">{loading ? 'Carregando...' : 'Sem dados'}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KpiCard title="Itens críticos" value={data.summary.criticalCount} tone="warning" />
                <KpiCard
                    title="Cobertura média (dias)"
                    value={data.summary.averageCoverageDays.toFixed(1)}
                    tone="info"
                />
                <KpiCard
                    title="Sugestões de compra"
                    value={(data.recommendations || []).length}
                    tone="primary"
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Itens críticos</h3>
                        <div className="overflow-x-auto mt-3">
                            <table className="table table-sm table-zebra">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Saldo</th>
                                        <th>Mínimo</th>
                                        <th>ROP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.criticalItems || []).map((item: any) => (
                                        <tr key={item.itemId}>
                                            <td>{item.name}</td>
                                            <td>{item.onHand}</td>
                                            <td>{item.minStock ?? '-'}</td>
                                            <td>{item.rop ?? '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Sugestões de compra</h3>
                        <div className="overflow-x-auto mt-3">
                            <table className="table table-sm table-zebra">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Saldo</th>
                                        <th>Sugerido</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.recommendations || []).map((item: any) => (
                                        <tr key={item.itemId}>
                                            <td>{item.name}</td>
                                            <td>{item.onHand}</td>
                                            <td>{Math.ceil(item.suggestedPurchase || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card sf-dashboard-panel">
                <div className="card-body">
                    <h3 className="text-lg font-semibold">Consumo por serviço (top 5)</h3>
                    <div className="overflow-x-auto mt-3">
                        <table className="table table-sm table-zebra">
                            <thead>
                                <tr>
                                    <th>Serviço</th>
                                    <th>Qtd</th>
                                    <th>Custo total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topServices.map((row) => (
                                    <tr key={row.servicoId}>
                                        <td>{serviceById[row.servicoId]?.name || row.servicoId}</td>
                                        <td>{row.totalQuantity}</td>
                                        <td>{row.totalCost.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
